'use client';

import 'core-js/features/array/to-reversed';
import 'core-js/features/array/to-spliced';
import 'core-js/features/array/to-sorted';
import 'core-js/features/array/with';

import { Suspense, type ReactNode } from 'react';

import { useReducedMotion } from '@react-spring/web';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Provider } from 'jotai';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { Tooltip } from 'radix-ui';

import { Toaster } from '@quick/components/Toaster';

import {
  HealthCheckAlert,
  VersionAlert,
  Boundary,
  Clarity,
  CookieNotice,
  CompatibilityNotice,
  DetectDVH,
  EnvTag,
  FontConfig,
  LangProvider,
  ThemeDetector,
  useValidateToken,
} from '@/components/core/AppLayout';
import VerifyRequiresNewPass from '@/components/core/AppLayout/VerifyRequiresNewPass';
import { NoSSR } from '@/components/core/NoSSR';
import { CountryProvider } from '@/layers/core/deal/services/CountryContext';

/// <reference types="dayjs/plugin/utc" />
/// <reference types="dayjs/plugin/timezone" />

dayjs.extend(utc);
dayjs.extend(timezone);

export function HooksCaller() {
  useValidateToken();
  useReducedMotion();

  return null;
}

export function ClientProviders({
  children,
  lang,
}: {
  children: ReactNode;
  lang?: string;
}) {
  return (
    <Provider>
      <NuqsAdapter>
        <CountryProvider>
          <LangProvider value={lang}>
            <Tooltip.Provider>
              <HooksCaller />
              <DetectDVH />
              <FontConfig />
              <ThemeDetector />
              <Clarity />
              <Boundary>
                <VerifyRequiresNewPass />
                <HealthCheckAlert />
                <VersionAlert />
                {children}
                <Suspense>
                  <NoSSR>
                    <CookieNotice />
                    <CompatibilityNotice />
                  </NoSSR>
                </Suspense>
              </Boundary>
              <EnvTag />
              <Toaster />
            </Tooltip.Provider>
          </LangProvider>
        </CountryProvider>
      </NuqsAdapter>
    </Provider>
  );
}
